import { useCallback, useMemo, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { rqlBuilder } from "@extrahorizon/javascript-sdk";

import { getExhClient } from "lib/extrahorizon/client";
import { ExhPatientData, PatientsListEntry } from "types/patient";

import { convertToPatientListEntry } from "../utils/patientConverters";

interface UseSearchPatientsResult {
  patients: PatientsListEntry[];
  loading: boolean;
  search: (searchTerm: string) => Promise<void>;
}

export const useSearchPatients = (): UseSearchPatientsResult => {
  const [exhPatients, setExhPatients] = useState<ExhPatientData[]>([]);
  const [loading, setLoading] = useState(false);
  const { showBoundary: onError } = useErrorBoundary();

  const search = useCallback(
    async (searchTerm: string) => {
      const trimmedTerm = searchTerm.trim();
      if (!trimmedTerm) {
        setExhPatients([]);
        return;
      }

      try {
        setLoading(true);
        const client = getExhClient();

        // Build RQL query with OR conditions for partial matching across all fields
        const rql = rqlBuilder()
          .or(
            rqlBuilder().like("data.name", trimmedTerm).intermediate(),
            rqlBuilder().like("data.nhs_number", trimmedTerm).intermediate(),
            rqlBuilder().like("data.hospital_number", trimmedTerm).intermediate(),
            rqlBuilder().like("data.entia_patient_id", trimmedTerm).intermediate()
          )
          .limit(100)
          .build();

        const result = await client.data.documents.find("patient", { rql });
        setExhPatients(result.data || []);
      } catch (err) {
        onError(err);
      } finally {
        setLoading(false);
      }
    },
    [onError]
  );

  // Convert ExH patients to PatientsListEntry format for the view
  const patients = useMemo<PatientsListEntry[]>(() => {
    return exhPatients.map(convertToPatientListEntry);
  }, [exhPatients]);

  return { patients, loading, search };
};
